import type {List} from 'immutable';

import {resource} from '@albert-io/json-api-framework/request/builder';

import {getTeacherAssignmentClassroomsQuery} from './assignmentClassrooms.queries';

/** Subset of the classroom model this file reads off the teacher's classrooms query. */
interface IdentifiedClassroom {
  getId: () => string;
}

/** Subset of the `assignment_v3` meta model — generated from JSON:API metadata config, so untyped. */
interface ClassroomCountedAssignment {
  getCountOfClassrooms: () => number;
}

/**
 * `count_of_classrooms` counts the join rows, so unlike anything else the front end can read it sees
 * classes the actor can't — the only way to tell "removing my classes empties this assignment" from
 * "another teacher's class stays on it".
 */
const getAssignmentClassroomCountQuery = (assignmentId: string) =>
  resource('assignment_v3')
    .mandarkEndpoint(['assignments_v3', assignmentId])
    .withMeta('assignment_v3');

/** The facts a cross-class delete can only learn by reading; the rest the acting row already knows. */
export interface FetchedAssignmentDeleteScope {
  /** The assignment's classes that the acting teacher teaches. */
  classroomIds: string[];
  /** Classes on the assignment across every teacher, including any the actor can't read. */
  totalClassroomCount: number;
}

/**
 * The facts a cross-class ("All Classes") delete decides on: which of the assignment's classes the
 * acting teacher teaches, and how many classes it carries in total.
 *
 * Re-dirtied before each read rather than served from the interest cache: the delete that follows is
 * irreversible, so a count left over from an earlier page view isn't good enough to decide on (and a
 * failed interest would otherwise resolve forever as an empty stub).
 */
export const fetchAssignmentDeleteScope = async (
  assignmentId: string
): Promise<FetchedAssignmentDeleteScope> => {
  const classroomsQuery = getTeacherAssignmentClassroomsQuery(assignmentId);
  const assignmentQuery = getAssignmentClassroomCountQuery(assignmentId);
  classroomsQuery.invalidateInterest();
  assignmentQuery.invalidateInterest();

  const [classrooms, assignment] = await Promise.all([
    classroomsQuery.getResourcePromise() as Promise<List<IdentifiedClassroom>>,
    assignmentQuery.getResourcePromise() as Promise<ClassroomCountedAssignment>
  ]);

  return {
    classroomIds: classrooms.toArray().map((classroom) => classroom.getId()),
    totalClassroomCount: assignment.getCountOfClassrooms()
  };
};
